import React from 'react';
import { Smartphone, LayoutTemplate, Eye, ListFilter, Info } from 'lucide-react';

const FioriWizard = ({ data = {}, setData }) => {
  const plataforma = data.fioriPlatform || 'ABAP';

  const handleToggle = (option) => {
    setData({
      ...data,
      fioriOptions: { ...data.fioriOptions, [option]: !data.fioriOptions[option] }
    });
  };

  return (
    <div className="animate-in fade-in slide-in-from-top-4 duration-500 text-left">

      {/* NOTA DEL CONSULTOR UX / FIORI */}
      <div className="bg-purple-900/10 border-l-4 border-purple-600 p-6 mb-10 rounded-r-3xl flex gap-4 items-start shadow-sm">
        <div className="bg-purple-600 p-2 rounded-xl text-white shadow-lg shadow-purple-500/20 shrink-0">
          <Smartphone size={20} />
        </div>
        <div>
          <p className="text-[11px] text-purple-800 font-black uppercase tracking-widest mb-1 italic">Fiori Elements Design Guide:</p>
          <p className="text-[11px] text-slate-600 leading-relaxed font-medium">
            Esta herramienta genera **Metadata Extensions (MDE)** y anotaciones de interfaz de usuario para SAP Fiori.
            El enfoque es "Metadata-Driven UI": usted define la estructura en el backend y el frontend se construye automáticamente.
            Elija el template (List Report u Object Page) y las capacidades de la aplicación. La IA generará las anotaciones
            <code className="bg-purple-100 px-1 rounded text-purple-700">@UI.lineItem</code>,
            <code className="bg-purple-100 px-1 rounded text-purple-700">@UI.selectionField</code> y la configuración del Servicio OData.
          </p>
        </div>
      </div>
      {/* ENTIDAD BASE */}
      <div className="bg-purple-900/10 p-6 rounded-[24px] border-b-4 border-purple-500 mb-8">
        <label className="text-[10px] font-black text-purple-400 uppercase block mb-4">
          Entidad / CDS Fuente
        </label>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-[9px] font-bold text-slate-500 uppercase block mb-1">
              {plataforma === 'ABAP' ? 'Nombre Vista CDS (ZC_...)' : 'Nombre Entidad'}
            </label>
            <input
              type="text"
              className="w-full p-3 bg-slate-800 border-none rounded-xl text-xs font-mono uppercase focus:ring-2 focus:ring-purple-500 outline-none text-purple-200"
              placeholder={plataforma === 'ABAP' ? 'ZC_SALESORDER' : 'SalesOrders'}
              value={data.fioriEntityName || ''}
              onChange={e => setData({ ...data, fioriEntityName: e.target.value })}
            />
          </div>
          <div>
            <label className="text-[9px] font-bold text-slate-500 uppercase block mb-1">
              {plataforma === 'ABAP' ? 'Tabla Fuente DDIC' : 'Namespace CDS'}
            </label>
            <input
              type="text"
              className="w-full p-3 bg-slate-800 border-none rounded-xl text-xs font-mono uppercase focus:ring-2 focus:ring-purple-500 outline-none text-purple-200"
              placeholder={plataforma === 'ABAP' ? 'VBAK' : 'com.orquest'}
              value={data.fioriSource || ''}
              onChange={e => setData({ ...data, fioriSource: e.target.value })}
            />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">

        {/* CONFIGURACIÓN DEL TEMPLATE Y SERVICIO */}
        <div className="bg-purple-900/10 p-8 rounded-[32px] text-white shadow-xl italic border-b-4 border-purple-500">
          <label className="text-[10px] font-purple text-purple-400 uppercase block mb-6 border-b pb-2 flex items-center gap-2 text-left">
            <LayoutTemplate size={14} /> Fiori Template & Service
          </label>

          <div className="space-y-5 text-left">
            <div className="text-left">
              <label className="text-[9px] font-bold text-slate-500 uppercase block mb-1">Nombre del Servicio OData</label>
              <input type="text" className="w-full p-3 bg-slate-800 border-none rounded-xl text-xs font-mono uppercase focus:ring-2 focus:ring-blue-500 outline-none text-blue-200"
                placeholder="Z_SALES_ORDER_SRV" value={data.fioriService || ''} onChange={e => setData({ ...data, fioriService: e.target.value })} />
            </div>

            <div className="text-left">
              <label className="text-[9px] font-bold text-slate-500 uppercase block mb-1">Application Type (Template)</label>
              <select className="w-full p-3 bg-slate-800 border-none rounded-xl text-xs font-bold outline-none focus:ring-1"
                value={data.fioriTemplate || 'List Report'} onChange={e => setData({ ...data, fioriTemplate: e.target.value })}>
                <option value="List Report">List Report (Grilla + Filtros)</option>
                <option value="Object Page">Object Page (Detalle de Objeto)</option>
                <option value="Analytical List Page">Analytical List Page (Gráficos)</option>
                <option value="Overview Page">Overview Page (Tarjetas/Dashboard)</option>
              </select>
            </div>
          </div>
        </div>

        {/* CAPACIDADES DE LA UI */}
        <div className="bg-purple-900/10 p-8 rounded-[32px] text-white shadow-xl italic border-b-4 border-purple-500">
          <label className="text-[10px] font-purple text-purple-400 uppercase block mb-6 border-b pb-2 flex items-center gap-2 text-left">
            <ListFilter size={14} /> UI Capabilities
          </label>
          <div className="space-y-4 text-left">
            <label className="flex items-center justify-between group cursor-pointer p-3 bg-slate-50 rounded-xl hover:bg-purple-50 transition-all">
              <span className="text-[10px] font-bold text-slate-600 uppercase">Habilitar Búsqueda (Search)</span>
              <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-purple-600 focus:ring-0"
                checked={data.fioriOptions?.search || false} onChange={() => handleToggle('search')} />
            </label>

            <label className="flex items-center justify-between group cursor-pointer p-3 bg-slate-50 rounded-xl hover:bg-purple-50 transition-all">
              <span className="text-[10px] font-bold text-slate-600 uppercase">Habilitar Creación/Borrado</span>
              <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-purple-600 focus:ring-0"
                checked={data.fioriOptions?.crud || false} onChange={() => handleToggle('crud')} />
            </label>

            <label className="flex items-center justify-between group cursor-pointer p-3 bg-slate-50 rounded-xl hover:bg-purple-50 transition-all">
              <span className="text-[10px] font-bold text-slate-600 uppercase">Soporte Draft (Borrador)</span>
              <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-purple-600 focus:ring-0"
                checked={data.fioriOptions?.draft || false} onChange={() => handleToggle('draft')} />
            </label>

            <label className="flex items-center justify-between group cursor-pointer p-3 bg-slate-50 rounded-xl hover:bg-purple-50 transition-all">
              <span className="text-[10px] font-bold text-slate-600 uppercase">Exportar a Excel</span>
              <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-purple-600 focus:ring-0"
                checked={data.fioriOptions?.excel || false} onChange={() => handleToggle('excel')} />
            </label>
          </div>
        </div>

      </div>
    </div>
  );
};

export default FioriWizard;